import React from "react";
import { useState } from "react";
import { useEffect } from "react";

import _ from 'lodash';
import cx from 'classnames';

import { useDispatch, useSelector } from "react-redux";
import { acquireLock, breakLock, releaseLock } from "actions/lock";
import TimeMe from 'timeme.js';

import LockService from "services/lock.service";

import styles from './lock.module.scss';
import { Button, Image, Modal } from "react-bootstrap";
import LockWorker from "./worker";

const LOCK_IDLE_TIMEOUT = 30; // in seconds
const LOCK_IDLE_RELEASE_TIMEOUT = 30 * 60 * 1000; // in milliseconds

export const LockManager = ({ reference, lock, canBreakLock }) => {
  const dispatch = useDispatch();
  const lockIdleRef = React.useRef(0);
  const lockWorkerRef = React.useRef(null);

  const [breakLockConfirmation, setBreakLockConfirmation] = useState(false);

  const lockReference = useSelector((state) => {
    const id = LockService.getId(reference);
    return _.get(state, `lock['${id}'].item`);
  });

  const lockedByMe = React.useMemo(() => {
    return _.get(lock, 'token') === LockService.getLock(reference);
  }, [lock, reference]);

  const lockLost = useSelector((state) => {
    const id = LockService.getId(reference);
    return _.get(state, `lock['${id}'].lockLost`);
  });

  const lockReleased = useSelector((state) => {
    const id = LockService.getId(reference);
    return _.get(state, `lock['${id}'].released`);
  });

  const acquiring =  useSelector((state) => {
    const id = LockService.getId(reference);
    return _.get(state, `lock['${id}'].acquiring`);
  });

  const message = React.useMemo(() => {
    return `${_.get(lock, 'ownedBy.firstName')} (${_.get(lock, 'ownedBy.email')}) is working on this site, and only one person can edit at a time.`
  }, [lock, reference]);

  useEffect(() => {
    console.log("Reference changed");
    dispatch(acquireLock(reference));

    return () => {
      dispatch(releaseLock(reference));
    }
  }, [reference]);

  useEffect(() => {
    TimeMe.initialize({
      idleTimeoutInSeconds: LOCK_IDLE_TIMEOUT, // seconds
    });

    TimeMe.callWhenUserReturns(() => {
      console.log("User returned");
      startIdleTimer();
    });

    initializeWorker();

    return () => {
      if(lockWorkerRef.current){
        console.log("Clear lock timer");
        destroyWorker();
      }
    }
    
  }, [reference, lockedByMe]);

  useEffect(() => {
    if(lockLost){
      LockService.removeLock(reference);
    }
    
  }, [lockLost]);

  const initializeWorker = () => {
    destroyWorker();
    lockWorkerRef.current = new LockWorker();
    lockWorkerRef.current.postMessage('start');

    lockWorkerRef.current.addEventListener('message', (e) => {
      switch(e.data){
        case 'checkLock': {
          checklockStatus();
          break;
        }

        default: 
      }
    });
  }

  const destroyWorker = () => {
    if(lockWorkerRef.current){
      lockWorkerRef.current.postMessage('stop');
      lockWorkerRef.current.terminate();

      lockWorkerRef.current = null;
    }
  }

  const checklockStatus = () => {
    console.log(TimeMe);

    const hasLock = LockService.hasLock(reference);
    if(!hasLock){
      return;
    }

    if(!TimeMe.isUserCurrentlyIdle){
      dispatch(acquireLock(reference));
    }

    if(!TimeMe.isUserCurrentlyIdle && TimeMe.isUserCurrentlyOnPage){
      startIdleTimer();
    }
  }

  const startIdleTimer = () => {
    console.log("Reseting idle timeout");
    clearIdleTimer();

    lockIdleRef.current = setTimeout(() => {
      const hasLock = LockService.hasLock(reference);
      if(!hasLock){
        return;
      }
      
      dispatch(releaseLock(reference));
    }, LOCK_IDLE_RELEASE_TIMEOUT);
  }

  const clearIdleTimer = () => {
    if(lockIdleRef.current){
      clearTimeout(lockIdleRef.current);
    }
  }

  const handleBreakLock = () => {
    setBreakLockConfirmation(true);
  }

  if(lockedByMe && lockReleased){
    return <LockReleaseDialog reference={reference} />;
  }

  if(lockedByMe && lockLost){
    return <LockLostDialog lock={lockReference} />;
  }

  if(lockedByMe || !lock || acquiring){
    return <div/>;
  }

  return (
    <div className={styles.lock}>
      <i className={cx(styles.lockIcon, 'cleverstory-lock')} />
      <div className={styles.message}>{message}</div>
      <div className={styles.actions}>
        {canBreakLock &&
          <Button className={styles.action} onClick={handleBreakLock}>Acquire Lock</Button>
        }
      </div>

      <BreakLockDialog show={breakLockConfirmation} 
        reference={reference}
        onClose={() => setBreakLockConfirmation(false)}
      />
    </div>
  );
}

export const EditorLockedDialog = ({error, onClose}) => {
  
  const name = _.get(error, 'lock.ownedBy.firstName');
  const email = _.get(error, 'lock.ownedBy.email');

  const lockedByYou = useSelector((state) => {
    const user = _.get(state, 'users.me.email');
    return user === email;
  });

  const message = React.useMemo(() => {
    if(lockedByYou){
      return 'This page is currently open in another tab or window.'
    }

    return `Hold on to your creative horses. This page is currently locked for editing by ${name} (${email}). You'll be able to dive back into editing as soon as ${name} wraps up their magic touches.`
  }, [email, name, lockedByYou]);

  const handleClose = () => {
    if(onClose){
      onClose();
    }
  }

  return (
    <Modal backdrop='static'
      className={styles.lockModal}
      contentClassName={styles.dialogContent}
      dialogClassName={styles.lockDialog}
      backdropClassName={styles.backdrop}
      show={true}
      centered size="lg"
    >
        <Modal.Body className={styles.dialogBody}>
          <div className={styles.message}>
            <div className={styles.left}>
              <h2 className={styles.title}>This page is locked</h2>
              <p>{message}</p>
              <footer className={styles.actions}>
                <Button className={styles.action} variant="primary" onClick={handleClose}>Done</Button>
              </footer>
            </div>
            <Image src={'/images/acquireLock.png'} width={300} className={styles.publishImage} />
          </div>
          
        </Modal.Body>
      </Modal>
  );
}

export const BreakLockDialog = ({show, reference, onConfirm, onClose}) => {
  const dispatch = useDispatch();

  const breaking = useSelector((state) => {
    const id = LockService.getId(reference);
    return _.get(state, `lock['${id}'].breaking`);
  });

  const broken = useSelector((state) => {
    const id = LockService.getId(reference);
    return _.get(state, `lock['${id}'].broken`);
  });

  const handleConfirm = () => {
    dispatch(breakLock(reference));
  }

  const handleClose = () => {
    if(onClose){
      onClose();
    }
  }

  useEffect(() => {
    if(!breaking && broken){
      window.location.reload();
    }
  }, [breaking, broken]);

  return (
    <Modal backdrop='static'
      className={styles.lockModal}
      contentClassName={styles.dialogContent}
      dialogClassName={styles.lockDialog}
      backdropClassName={styles.backdrop}
      show={show}
      centered size="lg"
    >
        <Modal.Body className={styles.dialogBody}>
          <div className={styles.message}>
            <div className={styles.left}>
              <h2 className={styles.title}>Are you sure you want to acquire the lock?</h2>
              <p>By locking this page, you'll temporarily restrict others from editing it.</p>
              <p>They can continue their creative work once you're done.</p>
              <footer className={styles.actions}>
                <Button className={styles.action} 
                  variant="primary" 
                  disabled={breaking}
                  onClick={handleConfirm}
                >
                  {breaking ? 'Acquiring Lock...' : 'Acquire Lock'} 
                </Button>
                <Button className={styles.action} 
                  variant="secondary" 
                  disabled={breaking}
                  onClick={handleClose}
                >
                  Done
                </Button>
              </footer>
            </div>
            <Image src={'/images/acquireLock.png'} width={300} className={styles.publishImage} />
          </div>
          
        </Modal.Body>
      </Modal>
  );
}

export const LockLostDialog = ({lock}) => {
  const name = _.get(lock, 'ownedBy.firstName');
  const email = _.get(lock, 'ownedBy.email');

  const lockedByYou = useSelector((state) => {
    const user = _.get(state, 'users.me.email');
    return user === email;
  });
  
  const message = React.useMemo(() => {
    if(lockedByYou){
      return 'You have taken control this page on another browser tab or window. Please close the editing session on the other tab or window.'
    }

    return `${name} (${email}) has taken control of this page. You'll be able to make edits once they finish their editing session.`
  }, [email, name, lockedByYou]);

  const handleDone = () => {
    window.location.reload();
  }

  return (
    <Modal backdrop='static'
      className={styles.lockModal}
      contentClassName={styles.dialogContent}
      dialogClassName={styles.lockDialog}
      backdropClassName={styles.backdrop}
      show={true}
      centered size="lg"
    >
        <Modal.Body className={styles.dialogBody}>
          <div className={styles.message}>
            <div className={styles.left}>
              <h2 className={styles.title}>This page is locked</h2>
              <p>{message}</p>
              <p>You can continue editing other pages.</p>
              <footer className={styles.actions}>
                <Button className={styles.action} 
                  variant="secondary" 
                  onClick={handleDone}
                >
                  Done
                </Button>
              </footer>
            </div>
            <Image src={'/images/acquireLock.png'} width={300} className={styles.publishImage} />
          </div>
          
        </Modal.Body>
      </Modal>
  );
}

export const LockReleaseDialog = ({ reference }) => {
  const dispatch = useDispatch();

  const acquiring = useSelector((state) => {
    const id = LockService.getId(reference);
    return _.get(state, `lock['${id}'].acquiring`);
  });

  const handleAcquireLock = () => {
    dispatch(acquireLock(reference));
  }

  return (
    <Modal backdrop='static'
      className={styles.lockModal}
      contentClassName={styles.dialogContent}
      dialogClassName={styles.lockDialog}
      backdropClassName={styles.backdrop}
      show={true}
      centered size="lg"
    >
        <Modal.Body className={styles.dialogBody}>
          <div className={styles.message}>
            <div className={styles.left}>
              <h2 className={styles.title}>Taking a break from editing?</h2>
              <p>It's been 30 minutes since your last interaction with the editor, so we've released the lock to keep things flowing.</p>
              <p>Don't worry, you can jump back into editing if nobody else is editing page. Or reacquire the lock if someone else has started.</p>
              <footer className={styles.actions}>
                <Button className={styles.action} 
                  variant="secondary" 
                  disabled={acquiring}
                  onClick={handleAcquireLock}
                >
                  {acquiring ? 'Acquiring Lock...' : 'Acquire Lock'}
                </Button>
              </footer>
            </div>
            <Image src={'/images/acquireLock.png'} width={300} className={styles.publishImage} />
          </div>
          
        </Modal.Body>
      </Modal>
  );
}